import { lazy, Suspense } from "react";
import { Route } from "react-router-dom";
import type { AuthContextType } from "../context/authContext";
import AdminAccessControl from "../pages/accessControl/AdminAccessControl";
import Calculation from "../pages/calculations/Calculation";
import Calculations from "../pages/calculations/Calculations";
import AdminCategories from "../pages/categories/AdminCategories";
import { AdminCategory } from "../pages/categories/AdminCategory";
import { EditAdminClient, NewAdminClient } from "../pages/clients/AdminClient";
import AdminClients from "../pages/clients/AdminClients";
import { EditAdminItem, NewAdminItem } from "../pages/items/AdminItem";
import AdminItems from "../pages/items/AdminItems";
import { EditAdminModule } from "../pages/modules/AdminModule";
import AdminModules from "../pages/modules/AdminModules";
import AdminModuleSettings from "../pages/modules/AdminModuleSettings";
import AdminProjects from "../pages/projects/AdminProjects";
import AdminUsageStatistics from "../pages/usageStatistics/AdminUsageStatistics";
import Project from "../pages/projects/Project";
import AdminDashboard from "../pages/admin/AdminDashboard";
import { hasAdminPermissions } from "./adminMenuEntries";
import { AdminUser } from "./users/AdminUser";
import Users from "./users/AdminUsers";

const AdminGeoBlocking = lazy(() => import("../pages/geoBlocking/AdminGeoBlocking"));
const AdminGeoBlockingSettings = lazy(() => import("../pages/geoBlocking/AdminGeoBlockingSettings"));

export const getAdminRoutes = (permissions: AuthContextType["permissions"]) => (
  <>
    {hasAdminPermissions(permissions) && <Route path="admin" element={<AdminDashboard />} />}

    {!!permissions.admin.canManageProjects && (
      <>
        <Route path="admin/projects" element={<AdminProjects />} />
        <Route path="admin/projects/:projectId" element={<Project isAdmin />} />
      </>
    )}

    {!!permissions.admin.canManageCalculations && (
      <>
        <Route path="admin/calculations" element={<Calculations isAdmin />} />
        <Route path="admin/calculations/:calculationId" element={<Calculation isAdmin />} />
      </>
    )}

    {!!permissions.admin.canManageModules && (
      <>
        <Route path="admin/modules" element={<AdminModules />} />
        <Route path="admin/modules/:moduleId" element={<EditAdminModule />} />
        <Route path="admin/modules/:moduleId/settings" element={<AdminModuleSettings />} />
      </>
    )}

    {!!permissions.admin.canManageCategories && (
      <>
        <Route path="admin/categories" element={<AdminCategories />} />
        <Route path="admin/categories/:categoryCode" element={<AdminCategory />} />
      </>
    )}

    {!!permissions.admin.canManageClients && (
      <>
        <Route path="admin/clients" element={<AdminClients />} />
        <Route path="admin/clients/new" element={<NewAdminClient />} />
        <Route path="admin/clients/:clientId" element={<EditAdminClient />} />
      </>
    )}

    {!!permissions.admin.canManageUsers && (
      <>
        <Route path="admin/users" element={<Users />} />
        <Route path="admin/users/:userId" element={<AdminUser />} />
      </>
    )}

    {!!permissions.admin.canManageItems && (
      <>
        <Route path="admin/items" element={<AdminItems />} />
        <Route path="admin/items/new" element={<NewAdminItem />} />
        <Route path="admin/items/:itemId" element={<EditAdminItem />} />
      </>
    )}

    {!!permissions.admin.canManageAccessControl && (
      <Route path="admin/access-control" element={<AdminAccessControl />} />
    )}

    {!!permissions.admin.canManageGeoBlocking && (
      <>
        <Route
          path="admin/geo-blocking"
          element={
            <Suspense fallback={null}>
              <AdminGeoBlocking />
            </Suspense>
          }
        />
        <Route
          path="admin/geo-blocking/settings"
          element={
            <Suspense fallback={null}>
              <AdminGeoBlockingSettings />
            </Suspense>
          }
        />
      </>
    )}

    {!!permissions.admin.canManageUsageStatistics && (
      <Route path="admin/usage-statistics" element={<AdminUsageStatistics />} />
    )}
  </>
);
